import { useState } from "react";
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";
import {
  Activity,
  BatteryMedium,
  Check,
  FileText,
  FlaskConical,
  Footprints,
  MessageCircle,
  Pill,
  Salad,
  Scale,
  Stethoscope,
} from "lucide-react";

import { PageHeader } from "@/components/app-shell/PageHeader";
import { cn } from "@/lib/cn";

type ScenarioKey = "normal" | "high" | "insufficient";

type Recommendation = {
  category: "Supplementation" | "Further testing" | "Diet" | "Lifestyle";
  title: string;
  description: string;
};

type MarkerStatus = "inRange" | "attention" | "notReported";
type GoalKey = "recovery" | "energy" | "weight";

type Marker = {
  name: string;
  category: "Hormones" | "Metabolic" | "Liver";
  value: string;
  unit: string;
  range: string;
  status: MarkerStatus;
  position: string;
  interpretation: string;
  goals: GoalKey[];
};

type Scenario = {
  label: string;
  labNote: string;
  interpretation: string;
  note: string;
  markers: Marker[];
  recommendations: Recommendation[];
};

const normalMarkers: Marker[] = [
  {
    name: "Total testosterone",
    category: "Hormones",
    value: "612",
    unit: "ng/dL",
    range: "264–916",
    status: "inRange",
    position: "54%",
    interpretation:
      "Within the laboratory interval. Your clinician interprets this with symptoms, treatment timing, and prior levels.",
    goals: ["recovery", "energy", "weight"],
  },
  {
    name: "Free testosterone",
    category: "Hormones",
    value: "14.8",
    unit: "ng/dL",
    range: "5.0–21.0",
    status: "inRange",
    position: "61%",
    interpretation:
      "Within the laboratory interval. This helps contextualize how much testosterone may be available to tissues.",
    goals: ["recovery", "energy", "weight"],
  },
  {
    name: "Estradiol",
    category: "Hormones",
    value: "31.2",
    unit: "pg/mL",
    range: "7.6–42.6",
    status: "inRange",
    position: "67%",
    interpretation:
      "Within the laboratory interval. Estradiol is reviewed alongside testosterone, symptoms, and current therapy.",
    goals: ["recovery", "energy"],
  },
  {
    name: "SHBG",
    category: "Hormones",
    value: "34",
    unit: "nmol/L",
    range: "16.5–55.9",
    status: "inRange",
    position: "44%",
    interpretation:
      "Within the laboratory interval. SHBG affects how much circulating testosterone is bound or available.",
    goals: ["energy", "weight"],
  },
  {
    name: "Glucose",
    category: "Metabolic",
    value: "92",
    unit: "mg/dL",
    range: "70–99",
    status: "inRange",
    position: "76%",
    interpretation:
      "Within the laboratory interval at collection. Fasting status and recent meals can affect interpretation.",
    goals: ["energy", "weight"],
  },
  {
    name: "Creatinine",
    category: "Metabolic",
    value: "0.96",
    unit: "mg/dL",
    range: "0.76–1.27",
    status: "inRange",
    position: "39%",
    interpretation:
      "Within the laboratory interval. Hydration, muscle mass, medicines, and kidney filtration provide additional context.",
    goals: ["recovery"],
  },
  {
    name: "ALT",
    category: "Liver",
    value: "28",
    unit: "IU/L",
    range: "0–44",
    status: "inRange",
    position: "64%",
    interpretation:
      "Within the laboratory interval. ALT is interpreted with AST, medicines, alcohol exposure, exercise, and prior results.",
    goals: ["recovery"],
  },
  {
    name: "AST",
    category: "Liver",
    value: "24",
    unit: "IU/L",
    range: "0–40",
    status: "inRange",
    position: "60%",
    interpretation:
      "Within the laboratory interval. AST is interpreted with ALT and can also change after strenuous exercise.",
    goals: ["recovery"],
  },
];

const scenarios: Record<ScenarioKey, Scenario> = {
  normal: {
    label: "In range",
    labNote: "No laboratory notes provided.",
    interpretation:
      "All eight illustrative markers are within the performing laboratory’s stated reference intervals. Your clinician still considers symptoms, medicines, prior results, and why the panel was ordered.",
    note: "Your illustrative hormone, metabolic, and liver markers are all within the laboratory’s stated reference intervals. We did not identify a laboratory flag requiring follow-up. Continue your current plan unless the care team gives you different instructions.",
    markers: normalMarkers,
    recommendations: [
      {
        category: "Supplementation",
        title: "No additions recommended",
        description: "Continue only supplements approved by your care team.",
      },
      {
        category: "Further testing",
        title: "Repeat when scheduled",
        description:
          "A future panel can show whether these values remain stable over time.",
      },
      {
        category: "Diet",
        title: "Keep a consistent baseline",
        description:
          "Follow your usual balanced diet unless your clinician advises otherwise.",
      },
      {
        category: "Lifestyle",
        title: "Track how you feel",
        description: "Note meaningful changes in symptoms, sleep, and energy.",
      },
    ],
  },
  high: {
    label: "Flagged high",
    labNote: "Result flagged high by the performing laboratory.",
    interpretation:
      "Seven illustrative markers are within range and ALT is flagged high. A flag is not a diagnosis; your clinician interprets it with symptoms, medicines, alcohol use, exercise, and prior results.",
    note: "The laboratory flagged the illustrative ALT result above its stated interval. The rest of this panel is within range. Do not change treatment on your own; the care team recommends reviewing possible contributors and deciding whether a repeat test is appropriate.",
    markers: normalMarkers.map((marker) =>
      marker.name === "ALT"
        ? {
            ...marker,
            value: "58",
            status: "attention" as const,
            position: "91%",
            interpretation:
              "Above the laboratory interval. Your clinician reviews ALT with AST, symptoms, medicines, alcohol exposure, exercise, and prior results before recommending action.",
          }
        : marker,
    ),
    recommendations: [
      {
        category: "Supplementation",
        title: "Do not add anything new",
        description:
          "Keep your current plan unless your clinician gives new instructions.",
      },
      {
        category: "Further testing",
        title: "Discuss a repeat liver panel",
        description:
          "The care team will decide whether and when ALT should be repeated.",
      },
      {
        category: "Diet",
        title: "Review possible contributors",
        description:
          "Share recent alcohol, diet, medicine, and supplement changes.",
      },
      {
        category: "Lifestyle",
        title: "Avoid changing treatment yourself",
        description:
          "Report symptoms and follow the clinician’s reviewed plan.",
      },
    ],
  },
  insufficient: {
    label: "Retest needed",
    labNote:
      "Collection device contains insufficient sample to perform the test.",
    interpretation:
      "The laboratory could not produce reportable marker values from this sample. This is a collection issue, not an abnormal medical result.",
    note: "The laboratory could not test this sample. No clinical interpretation is possible. The care team will arrange the appropriate recollection step.",
    markers: normalMarkers.map((marker) => ({
      ...marker,
      value: "—",
      status: "notReported" as const,
      position: "50%",
      interpretation:
        "The lab could not provide a value, so this marker cannot be interpreted. A replacement collection may be needed.",
    })),
    recommendations: [
      {
        category: "Supplementation",
        title: "Make no changes from this sample",
        description:
          "No results were produced to support a treatment or supplement change.",
      },
      {
        category: "Further testing",
        title: "Complete the replacement kit",
        description:
          "The care team will confirm the recollection instructions.",
      },
      {
        category: "Diet",
        title: "Keep your usual routine",
        description: "Do not alter food or supplements unless instructed.",
      },
      {
        category: "Lifestyle",
        title: "Plan an unhurried collection",
        description:
          "Tell the care team if the first collection was difficult.",
      },
    ],
  },
};

const goalLabels: Record<GoalKey, string> = {
  recovery: "Recovery",
  energy: "Energy",
  weight: "Weight goals",
};

const goalInsights: Record<Marker["name"], Partial<Record<GoalKey, string>>> = {
  "Total testosterone": {
    recovery:
      "Testosterone helps your clinician consider the hormonal environment for muscle maintenance and recovery. This in-range result is reassuring context, but it does not measure recovery directly.",
    energy:
      "Low testosterone can overlap with fatigue for some people. Your in-range value makes this one useful part of the energy conversation alongside sleep, nutrition, stress, and symptoms.",
    weight:
      "Testosterone can influence muscle mass and body composition over time. This result supports context for your plan, but it is not a measure of fat loss or metabolic progress by itself.",
  },
  "Free testosterone": {
    recovery:
      "Free testosterone estimates the portion more readily available to tissues. Your clinician may compare it with training recovery, strength changes, symptoms, and future trends.",
    energy:
      "This value adds detail beyond total testosterone when discussing fatigue or drive. An in-range result does not rule out sleep, nutrition, stress, or other causes of low energy.",
    weight:
      "Available testosterone can be relevant to maintaining lean mass during weight change. Progress still depends on nutrition, activity, treatment context, and trends over time.",
  },
  Estradiol: {
    recovery:
      "Estradiol contributes to bone, joint, and tissue health. Your clinician reads it with testosterone and symptoms rather than treating one in-range number as a recovery score.",
    energy:
      "Estradiol balance can overlap with mood and wellbeing. This result helps complete the hormone picture but cannot explain day-to-day energy on its own.",
  },
  SHBG: {
    energy:
      "SHBG affects how much testosterone is bound versus available. It helps your clinician interpret the hormone results together when discussing energy-related symptoms.",
    weight:
      "SHBG can vary with metabolic, thyroid, liver, and nutrition factors. It adds context to body-composition goals, but it is not a direct marker of weight-loss progress.",
  },
  Glucose: {
    energy:
      "Glucose is a snapshot of circulating blood sugar at collection. Meal timing and fasting status matter, so this single result cannot show daily energy swings.",
    weight:
      "Glucose offers one metabolic signal relevant to a weight plan. Long-term progress may require trends and other measures rather than one normal reading.",
  },
  Creatinine: {
    recovery:
      "Creatinine is affected by hydration, muscle mass, and recent hard exercise. Your clinician uses it to add kidney and training context when reviewing recovery.",
  },
  ALT: {
    recovery:
      "ALT is reviewed because medicines, alcohol, illness, and sometimes strenuous training can affect it. Your clinician compares it with AST, symptoms, and prior values before relating it to recovery.",
  },
  AST: {
    recovery:
      "AST can rise after strenuous exercise because it is also present in muscle. Your clinician reads it with ALT and recent training rather than assuming a single cause.",
  },
};

function ScrollReveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
      transition={{
        delay: reduceMotion ? 0 : delay,
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
      }}
      viewport={{ amount: 0.12, once: true }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      {children}
    </motion.div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 28,
    restDelta: 0.001,
  });

  return (
    <motion.div
      aria-hidden
      className="fixed inset-x-0 top-0 z-50 h-2 origin-left bg-accent"
      style={{ scaleX }}
    />
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "success" | "warning" | "neutral";
}) {
  return (
    <span
      className={cn(
        "shrink-0 px-9 py-7 font-mono text-10 uppercase",
        tone === "success" && "bg-success-wash text-success",
        tone === "warning" && "bg-warning-wash text-warning",
        tone === "neutral" && "bg-sunken text-ink-secondary",
      )}
    >
      {children}
    </span>
  );
}

function ClinicianAttribution() {
  return (
    <div className="flex items-center gap-12 border-b border-line-subtle pb-16">
      <div
        aria-hidden
        className="grid h-38 w-38 shrink-0 place-items-center bg-accent font-mono text-12 text-ink-onAccent"
      >
        AC
      </div>
      <div className="min-w-0">
        <div className="font-body text-14 text-ink">
          Alex Chen, NP · Clinical review
        </div>
        <div className="mt-2 font-body text-11 text-ink-muted">
          Example clinician review · Aug 31 at 2:14 PM
        </div>
      </div>
    </div>
  );
}

function ResultJourney({ scenarioKey }: { scenarioKey: ScenarioKey }) {
  const reduceMotion = useReducedMotion();
  const steps = [
    { label: "Sample collected", date: "Aug 26" },
    { label: "Lab received", date: "Aug 28" },
    {
      label:
        scenarioKey === "insufficient"
          ? "Sample could not be tested"
          : "Lab testing complete",
      date: "Aug 30",
    },
    {
      label:
        scenarioKey === "insufficient"
          ? "Care team notified"
          : "Clinician reviewed",
      date: "Aug 31",
    },
  ];

  return (
    <section aria-label="Test timeline" className="mt-20">
      <div className="mb-10 flex items-baseline justify-between gap-10">
        <h2 className="font-mono text-13 uppercase text-ink">Test timeline</h2>
        <span className="font-body text-11 text-ink-muted">
          4 of 4 complete
        </span>
      </div>
      <div className="overflow-x-auto border-y border-line-subtle bg-raised">
        <ol className="relative grid min-w-[56rem] grid-cols-4 px-10 py-13 sm:min-w-0">
          <motion.span
            aria-hidden
            className="absolute left-[12.5%] right-[12.5%] top-24 h-1 bg-line-strong"
            initial={{ scaleX: reduceMotion ? 1 : 0 }}
            style={{ originX: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ amount: 0.8, once: true }}
            whileInView={{ scaleX: 1 }}
          />
          {steps.map((step, index) => (
            <motion.li
              className="relative z-10 flex min-w-0 flex-col items-center px-8 text-center"
              initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
              key={step.label}
              transition={{
                delay: reduceMotion ? 0 : 0.16 + index * 0.1,
                duration: 0.35,
              }}
              viewport={{ amount: 0.8, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <span className="grid h-22 w-22 place-items-center border-4 border-raised bg-ink text-page">
                <Check aria-hidden size={11} strokeWidth={2} />
              </span>
              <div className="mt-8 font-body text-11 text-ink">
                {step.label}
              </div>
              <div className="mt-2 font-mono text-9 uppercase text-ink-muted">
                0{index + 1} · {step.date}
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ResultHero({ scenario }: { scenario: Scenario }) {
  const counts = {
    inRange: scenario.markers.filter((marker) => marker.status === "inRange")
      .length,
    attention: scenario.markers.filter(
      (marker) => marker.status === "attention",
    ).length,
    noResult: scenario.markers.filter(
      (marker) => marker.status === "notReported",
    ).length,
  };
  const total = scenario.markers.length;
  const summaries = [
    {
      label: "In range",
      count: counts.inRange,
      color: "bg-success",
      text: "text-success",
    },
    {
      label: "Needs attention",
      count: counts.attention,
      color: "bg-warning",
      text: "text-warning",
    },
    {
      label: "No result",
      count: counts.noResult,
      color: "bg-ink-muted",
      text: "text-ink-muted",
    },
  ].filter((summary) => summary.count > 0);
  const headline =
    counts.noResult === total
      ? "A new sample is needed"
      : counts.attention > 0
        ? `${counts.attention} result needs attention`
        : "All results are in range";

  return (
    <section className="border border-line-subtle bg-raised px-18 py-16">
      <div className="flex flex-col gap-10 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="font-mono text-10 uppercase text-ink-muted">
            Your result at a glance
          </div>
          <h2 className="mt-4 font-body text-20 text-ink">{headline}</h2>
        </div>
        <span className="font-body text-11 text-ink-muted">
          Tested Aug 30, 2026
        </span>
      </div>

      <div
        aria-label="Result distribution"
        className="mt-15 flex h-8 overflow-hidden bg-line-subtle"
      >
        {summaries.map((summary) => (
          <span
            aria-hidden
            className={summary.color}
            key={summary.label}
            style={{ width: `${(summary.count / total) * 100}%` }}
          />
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-x-18 gap-y-6">
        {summaries.map((summary) => (
          <div className="flex items-baseline gap-5" key={summary.label}>
            <span className={cn("font-mono text-13", summary.text)}>
              {summary.count}
            </span>
            <span className="font-body text-11 text-ink-muted">
              {summary.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PatientGoals({ scenario }: { scenario: Scenario }) {
  const reduceMotion = useReducedMotion();
  const goals: Array<{
    key: GoalKey;
    description: string;
    icon: React.ReactNode;
  }> = [
    {
      key: "recovery",
      description: "Muscle recovery, training readiness, and resilience",
      icon: <Activity aria-hidden size={18} strokeWidth={1.5} />,
    },
    {
      key: "energy",
      description: "Steady energy, focus, and day-to-day wellbeing",
      icon: <BatteryMedium aria-hidden size={18} strokeWidth={1.5} />,
    },
    {
      key: "weight",
      description: "Body composition and sustainable weight progress",
      icon: <Scale aria-hidden size={18} strokeWidth={1.5} />,
    },
  ];

  return (
    <section className="mt-30">
      <div className="mb-10 max-w-[68ch]">
        <div className="font-mono text-10 uppercase text-accent">
          Your goals
        </div>
        <h2 className="mt-4 font-body text-20 text-ink">
          How your results connect to what you want to improve
        </h2>
        <p className="mt-5 font-body text-12 leading-145 text-ink-muted">
          You told us you are working on recovery, energy, and weight. These
          results give your care team context for what to maintain and what may
          be worth improving. No single marker explains how you feel or measures
          progress on its own.
        </p>
      </div>

      <div className="grid gap-1 bg-line-subtle md:grid-cols-3">
        {goals.map((goal, index) => {
          const related = scenario.markers.filter((marker) =>
            marker.goals.includes(goal.key),
          );
          const attention = related.filter(
            (marker) => marker.status === "attention",
          ).length;
          const available = related.filter(
            (marker) => marker.status !== "notReported",
          ).length;
          const summary =
            available === 0
              ? "Review after your retest"
              : attention > 0
                ? `${attention} related result to discuss`
                : "A baseline to build on";

          return (
            <motion.article
              className="bg-raised p-16"
              initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
              key={goal.key}
              transition={{
                delay: reduceMotion ? 0 : index * 0.09,
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
              viewport={{ amount: 0.3, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between gap-10">
                <span className="grid h-32 w-32 place-items-center bg-ink text-page">
                  {goal.icon}
                </span>
                <span className="font-mono text-9 text-ink-muted">
                  0{index + 1}
                </span>
              </div>
              <h3 className="mt-14 font-body text-16 text-ink">
                {goalLabels[goal.key]}
              </h3>
              <p className="mt-3 min-h-34 font-body text-11 leading-145 text-ink-muted">
                {goal.description}
              </p>
              <div className="mt-12 border-t border-line-subtle pt-10">
                <div
                  className={cn(
                    "font-mono text-9 uppercase",
                    attention > 0 ? "text-warning" : "text-success",
                    available === 0 && "text-ink-muted",
                  )}
                >
                  {summary}
                </div>
                <div className="mt-7 flex flex-wrap gap-4">
                  {related.map((marker) => (
                    <span
                      className="bg-sunken px-6 py-4 font-body text-9 text-ink-secondary"
                      key={marker.name}
                    >
                      {marker.name}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

function BiomarkerResults({ scenario }: { scenario: Scenario }) {
  const reduceMotion = useReducedMotion();
  const [openInsight, setOpenInsight] = useState<{
    marker: string;
    goal: GoalKey;
  } | null>(null);
  const categories = ["Hormones", "Metabolic", "Liver"] as const;

  return (
    <section className="mt-30">
      <div className="mb-10">
        <h2 className="font-mono text-13 uppercase text-ink">
          Your biomarkers
        </h2>
      </div>

      <div className="grid gap-12">
        {categories.map((category, categoryIndex) => {
          const markers = scenario.markers.filter(
            (marker) => marker.category === category,
          );
          return (
            <motion.div
              className="overflow-hidden border border-line-subtle bg-raised"
              initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
              key={category}
              transition={{
                delay: reduceMotion ? 0 : categoryIndex * 0.08,
                duration: 0.35,
              }}
              viewport={{ amount: 0.08, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between gap-10 border-b border-line-subtle bg-sunken px-16 py-12">
                <h3 className="font-mono text-11 uppercase text-ink">
                  {category}
                </h3>
                <span className="font-body text-11 text-ink-muted">
                  {markers.length} markers
                </span>
              </div>

              <div>
                {markers.map((marker) => {
                  const tone =
                    marker.status === "attention"
                      ? "warning"
                      : marker.status === "notReported"
                        ? "neutral"
                        : "success";
                  return (
                    <div
                      className="border-b border-line-subtle last:border-0"
                      key={marker.name}
                    >
                      <div className="grid gap-14 p-16 md:grid-cols-[1.05fr_0.72fr_1.25fr_auto] md:items-center">
                        <div>
                          <div className="font-body text-14 text-ink">
                            {marker.name}
                          </div>
                          <div className="mt-3 font-mono text-9 uppercase text-ink-muted">
                            {category} marker
                          </div>
                        </div>

                        <div className="flex items-baseline gap-5">
                          <span className="font-mono text-22 text-ink">
                            {marker.value}
                          </span>
                          {marker.status !== "notReported" && (
                            <span className="font-body text-10 text-ink-muted">
                              {marker.unit}
                            </span>
                          )}
                        </div>

                        <div>
                          <div className="mb-7 flex items-center justify-between gap-10 font-body text-10 text-ink-muted">
                            <span>Lab interval</span>
                            <span>
                              {marker.range} {marker.unit}
                            </span>
                          </div>
                          <div className="relative h-8 bg-line-strong">
                            <motion.span
                              className="absolute left-[15%] h-full w-[70%] origin-left bg-success opacity-70"
                              initial={{ scaleX: reduceMotion ? 1 : 0 }}
                              transition={{ duration: 0.7 }}
                              viewport={{ amount: 0.8, once: true }}
                              whileInView={{ scaleX: 1 }}
                            />
                            {marker.status !== "notReported" && (
                              <span
                                aria-hidden
                                className={cn(
                                  "absolute top-1/2 h-18 w-4 -translate-x-1/2 -translate-y-1/2 border border-page",
                                  marker.status === "attention"
                                    ? "bg-warning"
                                    : "bg-ink",
                                )}
                                style={{ left: marker.position }}
                              />
                            )}
                          </div>
                        </div>

                        <Badge tone={tone}>
                          {marker.status === "attention"
                            ? "High"
                            : marker.status === "notReported"
                              ? "No result"
                              : "In range"}
                        </Badge>
                      </div>
                      <div className="border-t border-line-subtle bg-sunken px-16 py-10">
                        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                          <div className="font-mono text-9 uppercase text-ink-muted">
                            Clinician interpretation
                          </div>
                          <div className="flex flex-wrap gap-4">
                            {marker.goals.map((goal) => {
                              const isOpen =
                                openInsight?.marker === marker.name &&
                                openInsight.goal === goal;
                              return (
                                <button
                                  aria-controls={`insight-${marker.name}-${goal}`.replaceAll(
                                    " ",
                                    "-",
                                  )}
                                  aria-expanded={isOpen}
                                  className={cn(
                                    "border border-line-strong bg-raised px-4 py-2 font-mono text-7 uppercase text-ink-secondary transition-colors hover:border-ink hover:bg-ink hover:text-page focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
                                    isOpen && "border-ink bg-ink text-page",
                                  )}
                                  key={goal}
                                  onClick={() =>
                                    setOpenInsight(
                                      isOpen
                                        ? null
                                        : { marker: marker.name, goal },
                                    )
                                  }
                                  type="button"
                                >
                                  {goalLabels[goal]}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <p className="mt-4 max-w-[80ch] font-body text-11 leading-145 text-ink-secondary">
                          {marker.interpretation}
                        </p>
                        <AnimatePresence initial={false}>
                          {openInsight?.marker === marker.name && (
                            <motion.div
                              animate={{ height: "auto", opacity: 1 }}
                              className="overflow-hidden"
                              exit={{ height: 0, opacity: 0 }}
                              id={`insight-${marker.name}-${openInsight.goal}`.replaceAll(
                                " ",
                                "-",
                              )}
                              initial={{ height: 0, opacity: 0 }}
                              key={openInsight.goal}
                              transition={{
                                duration: reduceMotion ? 0 : 0.28,
                                ease: [0.22, 1, 0.36, 1],
                              }}
                            >
                              <div className="mt-10 border-l-4 border-accent bg-raised px-12 py-10">
                                <div className="font-mono text-9 uppercase text-accent">
                                  Why this relates to{" "}
                                  {goalLabels[openInsight.goal]}
                                </div>
                                <p className="mt-4 max-w-[76ch] font-body text-11 leading-150 text-ink-secondary">
                                  {
                                    goalInsights[marker.name]?.[
                                      openInsight.goal
                                    ]
                                  }
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ClinicianNoteCard({
  scenario,
  expanded = false,
}: {
  scenario: Scenario;
  expanded?: boolean;
}) {
  return (
    <div className="border border-line-subtle bg-raised">
      <div className="flex items-center justify-between gap-12 bg-ink px-15 py-13 text-page">
        <div className="flex items-center gap-9 font-mono text-11 uppercase">
          <Stethoscope aria-hidden size={16} strokeWidth={1.5} />
          Clinician note
        </div>
        <span className="font-body text-11 text-page">Aug 31, 2026</span>
      </div>
      <div className="p-20">
        <ClinicianAttribution />
        <div className="mt-22 font-mono text-11 uppercase text-ink-muted">
          {expanded ? "Summary" : "What this means for you"}
        </div>
        <p className="mt-10 font-body text-17 leading-155 text-ink-secondary">
          {expanded ? scenario.note : scenario.interpretation}
        </p>
        <div className="mt-20 border-l-4 border-accent bg-sunken px-14 py-12">
          <div className="font-mono text-10 uppercase text-ink-muted">
            Clinical guardrail
          </div>
          <p className="mt-5 font-body text-12 leading-145 text-ink-secondary">
            Do not change treatment from laboratory values alone. Follow the
            care team’s reviewed instructions.
          </p>
        </div>
      </div>
    </div>
  );
}

function SourceReportCard({
  onAction,
}: {
  onAction: (message: string) => void;
}) {
  return (
    <ScrollReveal className="mt-18">
      <section className="overflow-hidden border border-line-subtle bg-ink text-page sm:grid sm:grid-cols-[auto_1fr_auto] sm:items-center">
        <div className="grid min-h-full place-items-center bg-accent p-18 text-ink-onAccent">
          <FileText aria-hidden size={26} strokeWidth={1.25} />
        </div>
        <div className="p-16 sm:px-20">
          <div className="font-mono text-9 uppercase text-page">
            Source document · PDF
          </div>
          <h2 className="mt-4 font-body text-16 text-page">
            Original laboratory report
          </h2>
          <p className="mt-4 max-w-[54ch] font-body text-11 leading-145 text-page">
            The untouched report from the performing laboratory, finalized Aug
            30, 2026.
          </p>
        </div>
        <button
          className="m-16 border border-page bg-page px-13 py-10 font-mono text-9 uppercase text-ink transition-colors hover:bg-accent hover:text-ink-onAccent"
          onClick={() =>
            onAction("The original laboratory report would open here.")
          }
          type="button"
        >
          View report
        </button>
      </section>
    </ScrollReveal>
  );
}

function ResultHistory({ scenario }: { scenario: Scenario }) {
  const available = scenario.markers.filter(
    (marker) => marker.status !== "notReported",
  ).length;
  const attention = scenario.markers.filter(
    (marker) => marker.status === "attention",
  ).length;

  return (
    <section className="mt-34">
      <div className="mb-10 flex flex-wrap items-baseline justify-between gap-8">
        <h2 className="font-mono text-13 uppercase text-ink">
          Your testing history
        </h2>
        <span className="font-body text-11 text-ink-muted">
          First recorded result
        </span>
      </div>
      <div className="border border-line-subtle bg-raised p-20">
        <div className="relative min-h-[15rem] overflow-hidden bg-sunken px-18 py-20">
          <div className="absolute left-18 right-18 top-1/2 h-1 bg-line-strong" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <span className="mx-auto block h-16 w-16 border-4 border-page bg-accent" />
            <div className="mt-12 whitespace-nowrap font-mono text-11 uppercase text-ink">
              Aug 2026 · {available} biomarker results
            </div>
          </div>
        </div>
        <p className="mt-14 max-w-[60ch] font-body text-13 leading-145 text-ink-secondary">
          This is your first panel in System Labs. Future results can appear
          here for comparison.{" "}
          {attention > 0
            ? `${attention} marker is flagged for review.`
            : "No laboratory flags are shown in this illustrative panel."}
        </p>
      </div>
    </section>
  );
}

function ResultFaq() {
  const questions = [
    [
      "Does this panel explain everything?",
      "No. Laboratory results are one part of the clinical picture. Your clinician also considers symptoms, history, medicines, and prior trends.",
    ],
    [
      "Should I change treatment based on this result?",
      "No. Keep following your current plan unless your clinician gives you different instructions in the reviewed note or a secure message.",
    ],
    [
      "Why might my clinician repeat a marker?",
      "A repeat result can help confirm a flag and show whether a value is stable, improving, or changing over time.",
    ],
    [
      "What should I do if my symptoms changed?",
      "Send the care team a secure message with what changed, when it started, and any recent medicine or supplement changes.",
    ],
  ];

  return (
    <section className="mt-34">
      <h2 className="mb-10 font-mono text-13 uppercase text-ink">
        Questions patients ask
      </h2>
      <div className="border-t border-line-strong">
        {questions.map(([question, answer], index) => (
          <details className="group border-b border-line-strong" key={question}>
            <summary className="flex cursor-pointer list-none items-center gap-14 py-16 font-body text-14 text-ink">
              <span className="font-mono text-10 text-ink-muted">
                0{index + 1}
              </span>
              <span className="flex-1">{question}</span>
              <span
                aria-hidden
                className="font-mono text-18 text-ink-muted group-open:hidden"
              >
                +
              </span>
              <span
                aria-hidden
                className="hidden font-mono text-18 text-ink-muted group-open:inline"
              >
                −
              </span>
            </summary>
            <p className="max-w-[62ch] pb-18 pl-38 font-body text-13 leading-150 text-ink-secondary">
              {answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ClinicalChatCta({
  onAction,
}: {
  onAction: (message: string) => void;
}) {
  return (
    <div className="mt-1 flex flex-col gap-12 border border-t-0 border-line-subtle bg-sunken p-16 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-10">
        <span className="grid h-30 w-30 shrink-0 place-items-center bg-accent text-ink-onAccent">
          <MessageCircle aria-hidden size={15} strokeWidth={1.5} />
        </span>
        <div>
          <div className="font-body text-13 text-ink">
            Talk through your results
          </div>
          <div className="mt-3 max-w-[54ch] font-body text-11 leading-145 text-ink-muted">
            Your lab results and clinician review will be attached to the
            conversation.
          </div>
        </div>
      </div>
      <button
        className="shrink-0 border border-ink bg-ink px-13 py-10 font-mono text-9 uppercase text-page"
        onClick={() =>
          onAction(
            "Clinical chat would open with this result and review attached.",
          )
        }
        type="button"
      >
        Open clinical chat
      </button>
    </div>
  );
}

function Recommendations({ items }: { items: Recommendation[] }) {
  const reduceMotion = useReducedMotion();
  const icons: Record<Recommendation["category"], React.ReactNode> = {
    Supplementation: <Pill aria-hidden size={17} strokeWidth={1.5} />,
    "Further testing": <FlaskConical aria-hidden size={17} strokeWidth={1.5} />,
    Diet: <Salad aria-hidden size={17} strokeWidth={1.5} />,
    Lifestyle: <Footprints aria-hidden size={17} strokeWidth={1.5} />,
  };

  return (
    <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-1">
      <span
        aria-hidden
        className="absolute left-[12.5%] right-[12.5%] top-26 hidden h-1 bg-line-strong lg:block"
      />
      {items.map((item, index) => (
        <motion.div
          className="relative flex min-h-[15rem] flex-col border border-line-subtle bg-raised p-16"
          initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
          key={item.title}
          transition={{
            delay: reduceMotion ? 0 : index * 0.08,
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1],
          }}
          viewport={{ amount: 0.25, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="relative z-10 mb-18 flex items-center justify-between gap-10">
            <span className="grid h-30 w-30 place-items-center bg-ink text-page">
              {icons[item.category]}
            </span>
            <span className="font-mono text-9 text-ink-muted">
              {String(index + 1).padStart(2, "0")}
            </span>
          </div>
          <span className="font-mono text-9 uppercase text-accent">
            {item.category}
          </span>
          <div className="mt-auto">
            <strong className="block font-body text-13 font-medium text-ink">
              {item.title}
            </strong>
            <span className="mt-3 block font-body text-12 leading-145 text-ink-muted">
              {item.description}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export function LabResultsMockScreen() {
  const [scenarioKey, setScenarioKey] = useState<ScenarioKey>("normal");
  const [notice, setNotice] = useState("");
  const scenario = scenarios[scenarioKey];

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-page text-ink">
        <ScrollProgress />
        <div className="border-b border-line-subtle bg-raised px-15 py-12 md:px-40">
          <div className="mx-auto flex max-w-[65rem] flex-col gap-10 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-mono text-11 uppercase text-ink-muted">
              Patient results preview · synthetic data
            </span>
            <div
              aria-label="Preview result scenario"
              className="grid w-full min-w-0 max-w-full grid-cols-1 overflow-hidden sm:w-auto sm:grid-cols-3"
              role="group"
            >
              {(Object.entries(scenarios) as [ScenarioKey, Scenario][]).map(
                ([key, option]) => (
                  <button
                    aria-label={option.label}
                    aria-pressed={scenarioKey === key}
                    className={cn(
                      "w-full min-w-0 overflow-hidden border border-line-strong px-4 py-8 font-mono text-9 uppercase text-ink-secondary xs:px-8 xs:text-10",
                      scenarioKey === key && "border-ink bg-ink text-page",
                    )}
                    key={key}
                    onClick={() => setScenarioKey(key)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ),
              )}
            </div>
          </div>
        </div>

        <PageHeader
          title="Your lab results"
          description="Your sample was tested and reviewed by your System Labs care team."
        />

        <main className="mx-auto w-full max-w-[65rem] px-15 py-20 md:px-40 md:py-25">
          <div className="flex flex-col gap-5 bg-accent px-13 py-11 font-mono text-10 uppercase text-ink-onAccent sm:flex-row sm:items-center sm:justify-between">
            <span>Illustrative multi-marker panel · Y499 concept</span>
            <span>API-shaped synthetic data</span>
          </div>

          <ResultJourney scenarioKey={scenarioKey} />

          <section className="pt-24">
            <ScrollReveal>
              <ResultHero scenario={scenario} />
            </ScrollReveal>
            <PatientGoals scenario={scenario} />

            <ScrollReveal>
              <h2 className="mb-10 mt-30 font-mono text-13 uppercase text-ink">
                Clinician review
              </h2>
              <ClinicianNoteCard expanded scenario={scenario} />
              <ClinicalChatCta onAction={setNotice} />
            </ScrollReveal>

            <BiomarkerResults scenario={scenario} />
            <SourceReportCard onAction={setNotice} />

            <h2 className="mb-10 mt-30 font-mono text-13 uppercase text-ink">
              Your next steps
            </h2>
            <Recommendations items={scenario.recommendations} />
            <ScrollReveal>
              <ResultHistory scenario={scenario} />
            </ScrollReveal>
            <ScrollReveal>
              <ResultFaq />
            </ScrollReveal>
          </section>

          {notice && (
            <div
              aria-live="polite"
              className="mt-18 border border-line-strong bg-ink px-14 py-10 font-body text-12 text-page"
              role="status"
            >
              {notice}
            </div>
          )}

          <p className="mt-26 border-t border-line-subtle pt-14 font-body text-10 leading-150 text-ink-muted">
            This concept uses synthetic values. Production values, reference
            intervals, flags, clinician language, and signatures must come from
            the performing laboratory and verified clinical records.
          </p>
        </main>
      </div>
    </MotionConfig>
  );
}
